import { useTranslation } from '@cobliteam/i18n';
import { Tooltip, Typography } from '@cobliteam/ui/components';
import { cn } from '@cobliteam/ui/utils';
import { type CSSProperties, forwardRef, type ReactNode, useState } from 'react';
import { tv } from 'tailwind-variants';
import AIIcon from '../AIIcon';

/** `solid`: full gradient bg + white sparkles. `soft`: frosted gradient bg + gradient sparkles. `ghost`: no background. `ai-outline`: white bg + gradient border. */
export type AIButtonVariant = 'solid' | 'soft' | 'ghost' | 'ai-outline';

export type AIButtonSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

/** `rounded`/`circle`: icon-only. `cta`: wide button with sparkles + label. */
export type AIButtonShape = 'rounded' | 'circle' | 'cta';

export type AIButtonProps = {
  showTooltip?: boolean;
  className?: string;
  dataTestId?: string;
  disabled?: boolean;
  size?: AIButtonSize;
  style?: CSSProperties;
  /** Visual style. @default 'solid' */
  variant?: AIButtonVariant;
  /** Button shape / layout mode. @default 'rounded' */
  shape?: AIButtonShape;
  /** Text displayed when shape === 'cta' */
  label?: ReactNode;
  onClick?: () => void;
  isSelected?: boolean;
};

const ICON_SIZE_MAP: Record<AIButtonSize, number> = { sm: 32, md: 38, lg: 42, xl: 46, '2xl': 58 };

const AI_GRADIENT = 'linear-gradient(90deg, #0745cf 0%, #9847ee 100%)';

/** Frosted overlay — used for `soft` variant */
const SOFT_BACKGROUND_STYLE = {
  backgroundImage: `linear-gradient(90deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.85) 100%), ${AI_GRADIENT}`,
} as const;

const AI_OUTLINE_BACKGROUND_STYLE = {
  border: '1px solid transparent',
  backgroundImage: `linear-gradient(#fff, #fff), ${AI_GRADIENT}`,
  backgroundOrigin: 'border-box',
  backgroundClip: 'padding-box, border-box',
} as const;

function getVariantStyle(variant: AIButtonVariant, style?: CSSProperties): CSSProperties | undefined {
  if (variant === 'soft') return { ...SOFT_BACKGROUND_STYLE, ...style };
  if (variant === 'ai-outline') return { ...AI_OUTLINE_BACKGROUND_STYLE, ...style };
  return style;
}

// ─── CTA (shape === 'cta') ───────────────────────────────────────────────────

const aiButtonCtaStyles = tv({
  slots: {
    button:
      'group relative inline-flex pointer-events-auto h-10 shrink-0 cursor-pointer items-center justify-center gap-2 rounded-[10px] border border-[#0745cf] border-solid px-2 pr-4 py-2 shadow-[1px_5px_11px_0px_rgba(152,71,238,0.32)] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-55 disabled:shadow-none disabled:ring-0 disabled:saturate-50 disabled:focus-visible:ring-gray-6',
    label: 'relative z-[1] whitespace-nowrap',
  },
  variants: {
    variant: {
      solid: {
        button: 'bg-gradient-to-r from-[#0745cf] to-[#9847ee] text-white',
        label: 'text-white',
      },
      soft: {
        button: '',
        label: 'text-gray-12',
      },
      ghost: {
        button: 'border-transparent shadow-none',
        label: 'text-gray-11',
      },
      'ai-outline': {
        button: 'h-9 rounded-lg border-transparent bg-white text-[#0745cf] shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] hover:shadow-[0_1px_3px_0_rgba(16,24,40,0.1)]',
        label: 'bg-gradient-to-r from-[#0745cf] to-[#9847ee] bg-clip-text text-transparent',
      },
    },
  },
});

type AIButtonCtaSlotProps = {
  ariaLabel: string;
  className?: string;
  dataTestId?: string;
  decorPaused: boolean;
  isDisabled: boolean;
  isHovered: boolean;
  isSelected: boolean;
  label: ReactNode;
  style?: CSSProperties;
  variant: AIButtonVariant;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

const AIButtonCtaSlot = forwardRef<HTMLButtonElement, AIButtonCtaSlotProps>(function AIButtonCtaSlot(
  { ariaLabel, className, dataTestId, decorPaused, isDisabled, isHovered, isSelected, label, style, variant, onClick, onMouseEnter, onMouseLeave },
  ref
) {
  const isSolid = variant === 'solid';
  const { button, label: labelStyles } = aiButtonCtaStyles({ variant });
  return (
    <button
      aria-label={ariaLabel}
      aria-pressed={isSelected}
      className={cn(button(), className)}
      data-testid={dataTestId ?? 'ai-button-cta'}
      disabled={isDisabled}
      ref={ref}
      style={getVariantStyle(variant, style)}
      type='button'
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <AIIcon animationActive={decorPaused ? false : undefined} isHovered={isHovered} size={32} sparklesPaint={isSolid ? 'white' : 'brand-gradient'} />
      <Typography as='span' className={labelStyles()} variant='text-md-medium'>
        {label}
      </Typography>
    </button>
  );
});

AIButtonCtaSlot.displayName = 'AIButtonCtaSlot';

// ─── Icon-only (shape === 'rounded' | 'circle') ──────────────────────────────

const aiButtonIconStyles = tv({
  base: 'group relative inline-flex shrink-0 cursor-pointer items-center justify-center transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-55 disabled:shadow-none disabled:ring-0 disabled:saturate-50 disabled:focus-visible:ring-gray-6',
  variants: {
    variant: {
      solid: 'bg-gradient-to-r from-[#0745cf] to-[#9847ee] text-white shadow-[1px_5px_11px_0px_rgba(152,71,238,0.32)]',
      soft: '',
      ghost: '',
      'ai-outline': 'border border-transparent bg-white text-[#0745cf] shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] hover:shadow-[0_1px_3px_0_rgba(16,24,40,0.1)]',
    },
    size: {
      sm: 'size-8',
      md: 'size-10',
      lg: 'size-12',
      xl: 'size-14',
      '2xl': 'size-16',
    },
    shape: {
      rounded: '',
      circle: 'rounded-full',
      cta: '',
    },
  },
  compoundVariants: [
    { size: 'sm', shape: 'rounded', class: 'rounded-lg' },
    { size: 'md', shape: 'rounded', class: 'rounded-[10px]' },
    { size: 'lg', shape: 'rounded', class: 'rounded-xl' },
    { size: 'xl', shape: 'rounded', class: 'rounded-xl' },
    { size: '2xl', shape: 'rounded', class: 'rounded-xl' },
  ],
  defaultVariants: {
    variant: 'solid' as AIButtonVariant,
    size: 'md' as AIButtonSize,
    shape: 'rounded' as AIButtonShape,
  },
});

type AIButtonIconSlotProps = {
  ariaLabel: string;
  className?: string;
  dataTestId?: string;
  decorPaused: boolean;
  isDisabled: boolean;
  isHovered: boolean;
  isSelected: boolean;
  shape: AIButtonShape;
  size: AIButtonSize;
  style?: CSSProperties;
  variant: AIButtonVariant;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

const AIButtonIconSlot = forwardRef<HTMLButtonElement, AIButtonIconSlotProps>(function AIButtonIconSlot(
  { ariaLabel, className, dataTestId, decorPaused, isDisabled, isHovered, isSelected, shape, size, style, variant, onClick, onMouseEnter, onMouseLeave },
  ref
) {
  const useGradientSparkles = variant !== 'solid';
  return (
    <button
      aria-label={ariaLabel}
      aria-pressed={isSelected}
      className={cn(aiButtonIconStyles({ variant, size, shape }), className)}
      data-testid={dataTestId ?? 'ai-button'}
      disabled={isDisabled}
      ref={ref}
      style={getVariantStyle(variant, style)}
      type='button'
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <AIIcon animationActive={decorPaused ? false : undefined} isHovered={isHovered} size={ICON_SIZE_MAP[size]} sparklesPaint={useGradientSparkles ? 'brand-gradient' : 'white'} />
    </button>
  );
});

AIButtonIconSlot.displayName = 'AIButtonIconSlot';

// ─── AIButton ────────────────────────────────────────────────────────────────

const AIButton = forwardRef<HTMLButtonElement, AIButtonProps>(
  (
    {
      showTooltip = true,
      className,
      dataTestId,
      disabled = false,
      size = 'md',
      style,
      variant = 'solid',
      shape = 'rounded',
      label,
      onClick = () => {
        void 0;
      },
      isSelected = false,
    },
    ref
  ) => {
    const { t } = useTranslation('ai');
    const [isHovered, setIsHovered] = useState(false);

    const isDisabled = disabled;
    const decorPaused = isDisabled;

    const sharedSlotProps = {
      ref,
      className,
      dataTestId,
      decorPaused,
      isDisabled,
      isHovered,
      isSelected,
      style,
      variant,
      onClick,
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
    };

    if (shape === 'cta') {
      const ariaLabel = typeof label === 'string' ? label : t('assistant_float.aria_label');
      return (
        <Tooltip enable={showTooltip} content={t('assistant_float.label')} asChild>
          <AIButtonCtaSlot {...sharedSlotProps} ariaLabel={ariaLabel} label={label} />
        </Tooltip>
      );
    }

    return (
      <Tooltip enable={showTooltip} content={t('assistant_float.label')} asChild>
        <AIButtonIconSlot {...sharedSlotProps} ariaLabel={t('assistant_float.aria_label')} shape={shape} size={size} />
      </Tooltip>
    );
  }
);

AIButton.displayName = 'AIButton';

export default AIButton;
